import { useNuxtApp } from "#app";
import type { AxiosError, AxiosInstance } from "axios";
import axios from "axios";
import type GatheringFeedDetailPublic from "~/pages/gathering/feed/gathering-feed-detail-public.dto";
import type GatheringFeedPublic from "~/pages/gathering/feed/gathering-feed-public.dto";
import type GatheringPublic from "~/pages/gathering/gathering-public.dto";
import type {
  ContentReviewItem,
  ContentReviewListInfo,
  GatheringInfoForNonMember,
  MemberList,
} from "./types";
import type ContentForPublic from "~/pages/app/content/content-for-public.dto";
import type Content from "~/pages/app/content/content.dto";

const setInterceptors = (axiosInstance: AxiosInstance) => {
  const _axios = axiosInstance;
  _axios.interceptors.request.use((config) => {
    const accessToken = useAuthTokenStore().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  });
  _axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config!;
      const refreshToken = useAuthTokenStore().refreshToken;
      if (error.response?.status === 401 && refreshToken) {
        const res = await _axios.post("/auth/issue-access-token", {
          refreshToken,
        });
        const value: {
          result: boolean;
          fail?: { message: string };
          accessToken?: string;
        } = res.data.value;

        if (value.result) {
          const accessToken = value.accessToken!!;
          useAuthTokenStore().setAccessToken(accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return _axios(originalRequest);
        }
      }
    }
  );
};

const getAxios = (() => {
  let proxyAxios: AxiosInstance;
  return () => {
    if (useRuntimeConfig().public.USE_PROXY === "true" && !proxyAxios) {
      proxyAxios = axios.create();
      proxyAxios.interceptors.request.use((config) => {
        if (config.url && !config.url.startsWith("/api")) {
          config.url = "/api" + config.url;
        }
        return config;
      });
      setInterceptors(proxyAxios);
    }
    if (import.meta.client && useRuntimeConfig().public.USE_PROXY === "true") {
      return proxyAxios;
    } else {
      const nuxtApp = useNuxtApp();
      const $axios = nuxtApp.$axios as AxiosInstance;
      setInterceptors($axios);
      return $axios;
    }
  };
})();

const getGatheringInfo = async (gatheringId: number) => {
  try {
    const [response1, response2] = await Promise.all([
      getAxios().get("/gathering/info-public", {
        params: {
          gatheringId,
          scheduleCount: 10,
          feedCount: 100,
          voteCount: 10,
        },
      }),
      getAxios().get("/gathering/member/list", { params: { gatheringId } }),
    ]);
    const gatheringInfo = response1.data.value as GatheringInfoForNonMember;
    gatheringInfo.scheduleList.forEach((schedule) => {
      schedule.createdAt = new Date(schedule.createdAt);
      schedule.when = new Date(schedule.when);
      schedule.closingAt = new Date(schedule.closingAt || schedule.when);
    });
    gatheringInfo.feedList.forEach((feed) => {
      feed.createdAt = new Date(feed.createdAt);
    });
    if (gatheringInfo.notice) {
      gatheringInfo.notice.createdAt = new Date(gatheringInfo.notice.createdAt);
    }
    return {
      result: response1.data.result as boolean,
      value: {
        ...(response1.data.value as GatheringInfoForNonMember),
        memberInfo: {
          list: (response2.data.list as MemberList).map((member) => {
            member.visitAt = new Date(member.visitAt || 0);
            return member;
          }),
          capacity: response2.data.capacity as number,
        },
        suggestGatheringList: response1.data.value
          .suggestGatheringList as GatheringPublic[],
      },
    };
  } catch (err) {
    return {
      result: false,
      value: null,
    };
  }
};

const getEmoticonInfo = async (
  groupId: number
): Promise<{
  value: null | {
    groupId: number;
    name: string;
    price: number;
    mainImage: string;
    items: {
      orderId: number;
      thumbImage: string;
      image: string;
    }[];
  };
}> => {
  try {
    const response = await getAxios().get(
      "/inapp-purchase/emoticon/introduce",
      {
        params: { groupId },
      }
    );
    return {
      value: response.data.value,
    };
  } catch (err) {
    console.error(err);
    return {
      value: null,
    };
  }
};

const getAllGatheringIds = async () => {
  try {
    const response = await getAxios().get("/gathering/all-ids");
    return {
      list: response.data.list as number[],
    };
  } catch (err) {
    return {
      list: [],
    };
  }
};

const getGatheringList = async (
  interest: string[],
  limit: number,
  offset: number,
  seed: number,
  ranking = false
) => {
  const path = ranking
    ? "/gathering/list-public/ranking"
    : "/gathering/list-public";
  const response = await getAxios().get(path, {
    params: { interest, limit, offset, seed },
  });
  return {
    list: response.data.list as GatheringPublic[],
    done: response.data.done as boolean,
    nextOffset: response.data.nextOffset as number | null,
  };
};

const getGatheringFeedList = async (limit: number, offset: number) => {
  const response = await getAxios().get("/gathering/feed/list-public", {
    params: { limit, offset },
  });
  let list = response.data.list as GatheringFeedPublic[];
  return {
    list: list,
    done: response.data.done as boolean,
    nextOffset: response.data.nextOffset as number | null,
  };
};

const getGatheringFeedDetail = async (id: number) => {
  const response = await getAxios().get("/gathering/feed/public", {
    params: { feedId: id },
  });
  let value = response.data.value as GatheringFeedDetailPublic;
  return {
    result: response.data.result as boolean,
    fail: (response.data.fail || undefined) as { message: "없음" },
    value: value as GatheringFeedDetailPublic,
  };
};

const getContentForPublic = async (contentId: number) => {
  const response = await getAxios().get("/content/for-public", {
    params: { contentId },
  });
  const value = response.data.value as ContentForPublic;
  if (value) {
    value.scheduleAt = new Date(value.scheduleAt);
    value.paymentClosingAt = new Date(value.paymentClosingAt);
    value.memberInfo.imageList = value.memberInfo.imageList.map((image) => {
      if (image === "남성") {
        return "/images/il_user_male.svg";
      } else if (image === "여성") {
        return "/images/il_user_female.svg";
      } else {
        return image || "/images/profile_dummy.svg";
      }
    });
    value.host.image = value.host.image || "/images/profile_dummy.svg";
  }
  return {
    result: response.data.result as boolean,
    value: value,
  };
};

const getContent = async (id: number) => {
  const asyncData = await useAsyncData("content", async () => {
    const result = await getAxios().get("/content", {
      params: { id },
    });
    return result.data;
  });
  const data = await asyncData.data.value!!;
  const value = data.value as Content;
  if (value) {
    value.scheduleAt = new Date(value.scheduleAt);
    value.paymentClosingAt = new Date(value.paymentClosingAt);
    value.memberInfo.imageList = value.memberInfo.imageList.map((image) => {
      if (image === "남성") {
        return "/images/il_user_male.svg";
      } else if (image === "여성") {
        return "/images/il_user_female.svg";
      } else {
        return image || "/images/profile_dummy.svg";
      }
    });
    value.host.image = value.host.image || "/images/profile_dummy.svg";
  }
  return {
    result: data.result as boolean,
    value: value,
  };
};

const getContentV2 = async (id: number) => {
  const response = await getAxios().get("/content", {
    params: { id },
  });
  const data = response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: Content;
  };
  const value = data.value as Content;
  if (value) {
    value.scheduleAt = new Date(value.scheduleAt);
    value.paymentClosingAt = new Date(value.paymentClosingAt);
    value.memberInfo.imageList = value.memberInfo.imageList.map((image) => {
      if (image === "남성") {
        return "/images/il_user_male.svg";
      } else if (image === "여성") {
        return "/images/il_user_female.svg";
      } else {
        return image || "/images/profile_dummy.svg";
      }
    });
    value.host.image = value.host.image || "/images/profile_dummy.svg";
  }
  return data;
};

const getAccessToken = async (refreshToken: string) => {
  const response = await getAxios().post("/auth/issue-access-token", {
    refreshToken,
  });
  return response.data as {
    value: {
      result: boolean;
      fail?: {
        message: "토큰만료" | "토큰에러";
      };
      accessToken?: string;
    };
  };
};

const getUntransferredOEId = async (
  accessToken: string,
  type: "content" | "gathering",
  id: number
) => {
  const response = await getAxios().get("/my/oe/untransferred-id", {
    params: {
      type,
      id,
    },
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data as {
    result: boolean;
    fail?: {
      message: "기간마감" | "권한없음" | "이미전달됨";
    };
    value?: number;
  };
};

const getContentWishUserList = async (contentId: number) => {
  const response = await getAxios().get("/content/wish/list", {
    params: {
      contentId,
    },
  });
  return response.data as {
    list: {
      id: number;
      nickname: string;
      image: string;
    }[];
  };
};

const getContentWishUserListForPublic = async (contentId: number) => {
  const response = await getAxios().get("/content/wish/list/for-public", {
    params: {
      contentId,
    },
  });
  return response.data as {
    list: {
      id: number;
      nickname: string;
      image: string;
    }[];
  };
};

const toggleContentReviewLike = async (reviewId: number) => {
  const response = await getAxios().put("/content/review/like", {
    reviewId,
  });
  return response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: boolean;
  };
};

const toggleContentWish = async (contentId: number) => {
  const response = await getAxios().put("/content/wish", null, {
    params: {
      contentId,
    },
  });
  return response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: boolean;
  };
};

const getContentReviewList = async (
  count: number,
  page: number,
  hostId?: number
) => {
  const response = await getAxios().get("/content/review/list", {
    params: {
      count,
      page,
      hostId,
    },
  });
  const data = response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: ContentReviewListInfo;
  };
  if (data.value) {
    data.value.reviewList.forEach((review) => {
      review.createdAt = new Date(review.createdAt);
    });
  }
  return data;
};

const getContentReviewListForPublic = async (
  count: number,
  page: number,
  hostId?: number
) => {
  const response = await getAxios().get("/content/review/list/for-public", {
    params: {
      count,
      page,
      hostId,
    },
  });
  const data = response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: ContentReviewListInfo;
  };
  if (data.value) {
    data.value.reviewList.forEach((review) => {
      review.createdAt = new Date(review.createdAt);
    });
  }
  return data;
};

const getContentReviewListItem = async (reviewId: number) => {
  const response = await getAxios().get("/content/review/list", {
    params: {
      reviewId,
    },
  });
  return response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: ContentReviewItem;
  };
};

const cancelContent = async (contentId: number, reason: string) => {
  const response = await getAxios().put("/content/cancel", {
    contentId,
    reason,
  });
  return response.data as {
    result: boolean;
    fail?: {
      message: "권한없음" | "날짜지남";
    };
  };
};

const getContentMemberList = async (contentId: number) => {
  const response = await getAxios().get("/content/member/list", {
    params: {
      contentId,
    },
  });
  return response.data as {
    result: boolean;
    fail?: {
      message: "없음";
    };
    value?: {
      host: {
        id: number;
        image: null | string;
        name: string;
      };
      amIHost: boolean;
      info: {
        useMinimum: boolean;
        useGender: boolean;
        maxCapacity: number;
        minCapacity: number | null;
        maxMaleCapacity: number | null;
        maxFemaleCapacity: number | null;
        count: number;
        maleCount: number;
        femaleCount: number;
      };
      list: {
        id: number;
        nickname: string;
        image: null | string;
        friendCount: number;
      }[];
    };
  };
};

const visitContent = async (contentId: number) => {
  await getAxios().post("/content/visit", null, {
    params: {
      contentId,
    },
  });
};

const getChatHealth = async () => {
  return axios
    .get(`${useRuntimeConfig().public.CHAT_SERVER}/health`)
    .catch((error) => {
      if (error.response?.status === 503) {
        throw error;
      }
    });
};

const requestMyReferrerLink = async (
  source:
    | "gatheringMember_normal_share"
    | "gatheringGuest_normal_share"
    | "gatheringMember_contact_share"
    | "gatheringGuest_contact_share"
    | "content_normal_share"
    | "content_contact_share"
    | "moretab_contact_share"
    | "invite_gatheringWish"
    | "invite_contentWish"
    | "invite_frequentlyGathering"
    | "invite_frequentlyContent"
    | "invite_firstScheduleVisit"
    | "invite_gatheringCreated"
    | "invite_receiveGatheringMannerOe"
    | "invite_receiveContentMannerOe"
    | "invite_reviewGathering"
    | "invite_reviewContent",
  param: {
    contentId?: number;
    gathering?: number;
    targetPhoneNumber?: string;
    targetName?: string;
  }
) => {
  const response = await getAxios().post("/my/referrer-link", {
    source,
    ...param,
  });
  return response.data as {
    value: {
      message: string;
    };
  };
};

const getMyNudgeModal = async (
  type:
    | "invite_gatheringWish"
    | "invite_contentWish"
    | "invite_frequentlyGathering"
    | "invite_frequentlyContent"
    | "invite_firstScheduleVisit"
    | "invite_gatheringCreated"
    | "invite_receiveGatheringMannerOe"
    | "invite_receiveContentMannerOe"
    | "invite_reviewGathering"
    | "invite_reviewContent",
  param: {
    contentId?: number;
    gathering?: number;
    test?: boolean;
  }
) => {
  const response = await getAxios().get("/my/nudge-modal", {
    params: {
      type,
      ...param,
    },
  });
  return response.data as {
    value: {
      image: {
        uri: string;
        width: number;
        height: number;
      };
      title: {
        text: string;
        font_size: number;
        font_family: string;
        font_color: string;
        line_height: number;
        letter_spacing: number;
      };
      sub_title: {
        text: string;
        font_size: number;
        font_family: string;
        font_color: string;
        line_height: number;
        letter_spacing: number;
        highlight_text?: string;
        highlight_color?: string;
      };
      confirm_button: {
        text: string;
        font_size: number;
        font_family: string;
        font_color: string;
        background_color: string;
      };
      cancel_button: {
        text: string;
        font_size: number;
        font_family: string;
        font_color: string;
        background_color: string;
      };
    } | null;
  };
};

export default {
  getGatheringInfo,
  getAllGatheringIds,
  getEmoticonInfo,
  getGatheringList,
  getGatheringFeedList,
  getGatheringFeedDetail,
  getContent,
  getContentV2,
  getContentForPublic,
  getAccessToken,
  getUntransferredOEId,
  getContentWishUserList,
  getContentWishUserListForPublic,
  toggleContentReviewLike,
  toggleContentWish,
  getContentReviewList,
  getContentReviewListForPublic,
  cancelContent,
  getContentMemberList,
  visitContent,
  getChatHealth,
  requestMyReferrerLink,
  getMyNudgeModal,
};
